/**
 * Zod Validation Schemas
 */
import { z } from 'zod';
import { UserRole, DashboardType, AccountStatus, DealStage, TicketStatus, Priority, InvoiceStatus, InvoiceItemType, PaymentMethod, ContractStatus, BillingFrequency, RecurringScheduleStatus, AlertType, AlertSeverity, ExpenseRecurrence, SubscriptionBillingCycle, SubscriptionStatus, LoanType, LoanStatus, ChargeAccountType, ChargeAccountStatus, BudgetPeriodType, ActivityType, ApprovalStatus, ApprovalAction, ApprovalEntityType, ReportDataSource, ProjectType, ProjectStatus, ProjectBillingType, PhaseStatus, ProjectTaskStatus, RequirementCategory, RequirementStatus, RequirementLinkType, RiskCategory, RiskStatus, RiskLikelihood, DeliverableStatus, DigestFrequency, EscalationType, EscalationAction, KbArticleStatus, TicketLinkType, AppOperationalStatus, IncidentSeverity, IncidentStatus, MaintenanceStatus, AutomationTrigger, AutomationOperator, AutomationActionType, OtherIncomeType, } from '../constants/index.js';
// ============================================
// Common Schemas
// ============================================
export const uuidSchema = z.string().uuid();
export const paginationSchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(20),
});
export const searchSchema = z.object({
    search: z.string().optional(),
});
// ============================================
// Auth Schemas
// ============================================
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    firstName: z.string().min(1, 'First name is required').max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
});
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
export const forgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address'),
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
})
    .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
});
export const resetPasswordSchema = z.object({
    token: z.string().min(1, 'Token is required'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
});
// ============================================
// User Schemas
// ============================================
export const updateUserSchema = z.object({
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
    email: z.string().email().optional(),
});
const dashboardAccessEnum = z.enum([
    DashboardType.BILLING_FINANCE,
    DashboardType.TAX_COMPLIANCE,
    DashboardType.CUSTOMER_OPERATIONS,
    DashboardType.HELPDESK_SUPPORT,
    DashboardType.PROJECT_MANAGEMENT,
    DashboardType.ANALYTICS,
    DashboardType.SYSTEM_ADMIN,
]);
export const updateUserAdminSchema = updateUserSchema.extend({
    role: z.enum([UserRole.ADMIN, UserRole.MANAGER, UserRole.USER]).optional(),
    permissions: z.array(z.string()).optional(),
    dashboardAccess: z.array(dashboardAccessEnum).optional(),
    isActive: z.boolean().optional(),
});
export const createUserAdminSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    firstName: z.string().min(1, 'First name is required').max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
    role: z.enum([UserRole.ADMIN, UserRole.MANAGER, UserRole.USER]),
    permissions: z.array(z.string()).optional(),
    dashboardAccess: z.array(dashboardAccessEnum).optional(),
    isActive: z.boolean().optional().default(true),
});
// ============================================
// Account Schemas
// ============================================
export const createAccountSchema = z.object({
    name: z.string().min(1, 'Account name is required').max(255),
    industry: z.string().max(100).optional().nullable(),
    website: z.string().url().max(255).optional().nullable(),
    phone: z.string().max(50).optional().nullable(),
    addressLine1: z.string().max(255).optional().nullable(),
    addressLine2: z.string().max(255).optional().nullable(),
    city: z.string().max(100).optional().nullable(),
    state: z.string().max(100).optional().nullable(),
    postalCode: z.string().max(20).optional().nullable(),
    country: z.string().max(100).default('USA'),
    accountNumber: z.string().max(50).optional().nullable(),
    ownerId: z.string().uuid().optional().nullable(),
});
export const updateAccountSchema = createAccountSchema.partial().extend({
    status: z
        .enum([
        AccountStatus.PROSPECT,
        AccountStatus.ACTIVE,
        AccountStatus.CHURNED,
        AccountStatus.INACTIVE,
    ])
        .optional(),
    healthScore: z.number().int().min(0).max(100).optional().nullable(),
    annualRevenue: z.number().positive().optional().nullable(),
    employeeCount: z.number().int().positive().optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const listAccountsSchema = paginationSchema.merge(searchSchema).extend({
    status: z
        .enum([
        AccountStatus.PROSPECT,
        AccountStatus.ACTIVE,
        AccountStatus.CHURNED,
        AccountStatus.INACTIVE,
    ])
        .optional(),
    ownerId: z.string().uuid().optional(),
});
// ============================================
// Contact Schemas
// ============================================
export const createContactSchema = z.object({
    accountId: z.string().uuid('Invalid account ID'),
    email: z.string().email('Invalid email address').max(255),
    firstName: z.string().min(1, 'First name is required').max(100),
    lastName: z.string().min(1, 'Last name is required').max(100),
    phone: z.string().max(50).optional().nullable(),
    mobile: z.string().max(50).optional().nullable(),
    jobTitle: z.string().max(100).optional().nullable(),
    department: z.string().max(100).optional().nullable(),
    isPrimary: z.boolean().default(false),
});
export const updateContactSchema = createContactSchema.partial().extend({
    isActive: z.boolean().optional(),
    notes: z.string().optional().nullable(),
});
// ============================================
// Deal Schemas
// ============================================
export const createDealSchema = z.object({
    accountId: z.string().uuid('Invalid account ID'),
    contactId: z.string().uuid().optional().nullable(),
    title: z.string().min(1, 'Deal title is required').max(255),
    description: z.string().optional().nullable(),
    value: z.number().nonnegative().default(0),
    stage: z
        .enum([
        DealStage.LEAD,
        DealStage.QUALIFIED,
        DealStage.PROPOSAL,
        DealStage.NEGOTIATION,
        DealStage.CLOSED_WON,
        DealStage.CLOSED_LOST,
    ])
        .default(DealStage.LEAD),
    probability: z.number().int().min(0).max(100).optional(),
    expectedCloseDate: z.coerce.date().optional().nullable(),
    dealType: z.string().max(50).optional().nullable(),
    ownerId: z.string().uuid().optional().nullable(),
});
export const updateDealSchema = createDealSchema.partial().omit({ accountId: true }).extend({
    lostReason: z.string().max(255).optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const listDealsSchema = paginationSchema.extend({
    stage: z
        .enum([
        DealStage.LEAD,
        DealStage.QUALIFIED,
        DealStage.PROPOSAL,
        DealStage.NEGOTIATION,
        DealStage.CLOSED_WON,
        DealStage.CLOSED_LOST,
    ])
        .optional(),
    ownerId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
});
// ============================================
// Ticket Schemas
// ============================================
export const createTicketSchema = z.object({
    accountId: z.string().uuid('Invalid account ID'),
    contactId: z.string().uuid().optional().nullable(),
    subject: z.string().min(1, 'Subject is required').max(255),
    description: z.string().optional().nullable(),
    priority: z
        .enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT])
        .default(Priority.MEDIUM),
    category: z.string().max(100).optional().nullable(),
});
export const updateTicketSchema = z.object({
    status: z
        .enum([
        TicketStatus.OPEN,
        TicketStatus.IN_PROGRESS,
        TicketStatus.WAITING_CUSTOMER,
        TicketStatus.RESOLVED,
        TicketStatus.CLOSED,
    ])
        .optional(),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).optional(),
    assigneeId: z.string().uuid().optional().nullable(),
    category: z.string().max(100).optional().nullable(),
    tags: z.array(z.string()).optional(),
});
export const listTicketsSchema = paginationSchema.merge(searchSchema).extend({
    status: z
        .enum([
        TicketStatus.OPEN,
        TicketStatus.IN_PROGRESS,
        TicketStatus.WAITING_CUSTOMER,
        TicketStatus.RESOLVED,
        TicketStatus.CLOSED,
    ])
        .optional(),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).optional(),
    assigneeId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
});
export const createTicketMessageSchema = z.object({
    content: z.string().min(1, 'Message content is required'),
    isInternal: z.boolean().default(false),
});
// ============================================
// Note & Task Schemas
// ============================================
export const createNoteSchema = z
    .object({
    content: z.string().min(1, 'Note content is required'),
    accountId: z.string().uuid().optional().nullable(),
    contactId: z.string().uuid().optional().nullable(),
    dealId: z.string().uuid().optional().nullable(),
    ticketId: z.string().uuid().optional().nullable(),
    isPinned: z.boolean().default(false),
})
    .refine((data) => data.accountId || data.contactId || data.dealId || data.ticketId, 'Note must be attached to at least one entity');
export const createTaskSchema = z.object({
    title: z.string().min(1, 'Task title is required').max(255),
    description: z.string().optional().nullable(),
    dueDate: z.coerce.date().optional().nullable(),
    priority: z
        .enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT])
        .default(Priority.MEDIUM),
    assigneeId: z.string().uuid().optional().nullable(),
    accountId: z.string().uuid().optional().nullable(),
    contactId: z.string().uuid().optional().nullable(),
    dealId: z.string().uuid().optional().nullable(),
});
export const updateTaskSchema = createTaskSchema.partial().extend({
    completedAt: z.coerce.date().optional().nullable(),
});
// ============================================
// Finance Schemas
// ============================================
// Business Profile
export const createBusinessProfileSchema = z.object({
    name: z.string().min(1, 'Business name is required').max(255),
    addressLine1: z.string().max(255).optional().nullable(),
    addressLine2: z.string().max(255).optional().nullable(),
    city: z.string().max(100).optional().nullable(),
    state: z.string().max(100).optional().nullable(),
    zip: z.string().max(20).optional().nullable(),
    country: z.string().max(100).default('USA'),
    phone: z.string().max(50).optional().nullable(),
    email: z.string().email().max(255).optional().nullable(),
    website: z.string().max(255).optional().nullable(),
    taxId: z.string().max(50).optional().nullable(),
    logoUrl: z.string().max(500).optional().nullable(),
    isDefault: z.boolean().default(false),
});
export const updateBusinessProfileSchema = createBusinessProfileSchema.partial().extend({
    isActive: z.boolean().optional(),
});
// Application
export const createApplicationSchema = z.object({
    name: z.string().min(1, 'Application name is required').max(255),
    domain: z.string().max(255).optional().nullable(),
    description: z.string().optional().nullable(),
    businessProfileId: z.string().uuid().optional().nullable(),
});
export const updateApplicationSchema = createApplicationSchema.partial().extend({
    isActive: z.boolean().optional(),
});
export const listApplicationsSchema = paginationSchema.extend({
    businessProfileId: z.string().uuid().optional(),
    isActive: z.coerce.boolean().optional(),
});
// Invoice Item (embedded in invoice create/update)
export const invoiceItemSchema = z.object({
    description: z.string().min(1, 'Description is required').max(500),
    quantity: z.number().positive().default(1),
    unitPrice: z.number().nonnegative(),
    sortOrder: z.number().int().nonnegative().default(0),
    itemType: z
        .enum([
        InvoiceItemType.SUBSCRIPTION,
        InvoiceItemType.CONSULTING,
        InvoiceItemType.SERVICE,
        InvoiceItemType.PRODUCT,
        InvoiceItemType.OTHER,
    ])
        .default(InvoiceItemType.SERVICE),
});
// Invoice
export const createInvoiceSchema = z.object({
    businessProfileId: z.string().uuid('Invalid business profile'),
    accountId: z.string().uuid('Invalid account'),
    applicationId: z.string().uuid().optional().nullable(),
    recurringScheduleId: z.string().uuid().optional().nullable(),
    contractId: z.string().uuid().optional().nullable(),
    issueDate: z.coerce.date().optional(),
    dueDate: z.coerce.date(),
    taxRate: z.number().min(0).max(1).default(0),
    notes: z.string().optional().nullable(),
    paymentTerms: z.string().max(100).default('Net 30'),
    items: z.array(invoiceItemSchema).min(1, 'At least one line item is required'),
});
export const updateInvoiceSchema = createInvoiceSchema.partial();
export const listInvoicesSchema = paginationSchema.merge(searchSchema).extend({
    status: z
        .enum([
        InvoiceStatus.DRAFT,
        InvoiceStatus.SENT,
        InvoiceStatus.PAID,
        InvoiceStatus.PARTIAL,
        InvoiceStatus.OVERDUE,
        InvoiceStatus.CANCELLED,
        InvoiceStatus.VOID,
    ])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
});
export const updateInvoiceStatusSchema = z.object({
    status: z.enum([
        InvoiceStatus.DRAFT,
        InvoiceStatus.SENT,
        InvoiceStatus.PAID,
        InvoiceStatus.PARTIAL,
        InvoiceStatus.OVERDUE,
        InvoiceStatus.CANCELLED,
        InvoiceStatus.VOID,
    ]),
});
// Payment
export const createPaymentSchema = z.object({
    invoiceId: z.string().uuid('Invalid invoice ID'),
    amount: z.number().positive('Amount must be greater than zero'),
    paymentDate: z.coerce.date().optional(),
    paymentMethod: z
        .enum([
        PaymentMethod.CHECK,
        PaymentMethod.ACH,
        PaymentMethod.WIRE,
        PaymentMethod.CREDIT_CARD,
        PaymentMethod.CASH,
        PaymentMethod.OTHER,
    ])
        .default(PaymentMethod.CHECK),
    referenceNumber: z.string().max(100).optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const listPaymentsSchema = paginationSchema.extend({
    invoiceId: z.string().uuid().optional(),
});
// Other Income
export const createOtherIncomeSchema = z.object({
    businessProfileId: z.string().uuid('Invalid business profile'),
    description: z.string().min(1, 'Description is required').max(500),
    amount: z.number().positive('Amount must be greater than zero'),
    incomeDate: z.coerce.date().optional(),
    incomeType: z
        .enum([
        OtherIncomeType.FREELANCE,
        OtherIncomeType.CONSULTING,
        OtherIncomeType.INTEREST,
        OtherIncomeType.REFUND,
        OtherIncomeType.GRANT,
        OtherIncomeType.ROYALTY,
        OtherIncomeType.RENTAL,
        OtherIncomeType.COMMISSION,
        OtherIncomeType.TIP,
        OtherIncomeType.REIMBURSEMENT,
        OtherIncomeType.OTHER,
    ])
        .default(OtherIncomeType.OTHER),
    paymentMethod: z
        .enum([
        PaymentMethod.CHECK,
        PaymentMethod.ACH,
        PaymentMethod.WIRE,
        PaymentMethod.CREDIT_CARD,
        PaymentMethod.CASH,
        PaymentMethod.ZELLE,
        PaymentMethod.OTHER,
    ])
        .optional()
        .nullable(),
    referenceNumber: z.string().max(100).optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateOtherIncomeSchema = createOtherIncomeSchema.partial();
export const listOtherIncomeSchema = paginationSchema.merge(searchSchema).extend({
    incomeType: z
        .enum([
        OtherIncomeType.FREELANCE,
        OtherIncomeType.CONSULTING,
        OtherIncomeType.INTEREST,
        OtherIncomeType.REFUND,
        OtherIncomeType.GRANT,
        OtherIncomeType.ROYALTY,
        OtherIncomeType.RENTAL,
        OtherIncomeType.COMMISSION,
        OtherIncomeType.TIP,
        OtherIncomeType.REIMBURSEMENT,
        OtherIncomeType.OTHER,
    ])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
});
// ============================================
// Contract Schemas
// ============================================
export const createContractSchema = z.object({
    accountId: z.string().uuid('Invalid account'),
    businessProfileId: z.string().uuid('Invalid business profile'),
    title: z.string().min(1, 'Contract title is required').max(255),
    description: z.string().optional().nullable(),
    startDate: z.coerce.date(),
    endDate: z.coerce.date().optional().nullable(),
    autoRenew: z.boolean().default(false),
    renewalPeriodMonths: z.number().int().positive().optional().nullable(),
    status: z
        .enum([
        ContractStatus.DRAFT,
        ContractStatus.ACTIVE,
        ContractStatus.EXPIRED,
        ContractStatus.TERMINATED,
        ContractStatus.CANCELLED,
    ])
        .default(ContractStatus.DRAFT),
    notes: z.string().optional().nullable(),
});
export const updateContractSchema = createContractSchema.partial();
export const listContractsSchema = paginationSchema.merge(searchSchema).extend({
    accountId: z.string().uuid().optional(),
    status: z
        .enum([
        ContractStatus.DRAFT,
        ContractStatus.ACTIVE,
        ContractStatus.EXPIRED,
        ContractStatus.TERMINATED,
        ContractStatus.CANCELLED,
    ])
        .optional(),
});
// ============================================
// Recurring Schedule Schemas
// ============================================
export const recurringScheduleItemSchema = z.object({
    description: z.string().min(1, 'Description is required').max(500),
    quantity: z.number().positive().default(1),
    unitPrice: z.number().nonnegative(),
    sortOrder: z.number().int().nonnegative().default(0),
    itemType: z
        .enum([
        InvoiceItemType.SUBSCRIPTION,
        InvoiceItemType.CONSULTING,
        InvoiceItemType.SERVICE,
        InvoiceItemType.PRODUCT,
        InvoiceItemType.OTHER,
    ])
        .default(InvoiceItemType.SERVICE),
});
export const createRecurringScheduleSchema = z.object({
    accountId: z.string().uuid('Invalid account'),
    businessProfileId: z.string().uuid('Invalid business profile'),
    contractId: z.string().uuid().optional().nullable(),
    applicationId: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Schedule name is required').max(255),
    frequency: z
        .enum([
        BillingFrequency.MONTHLY,
        BillingFrequency.QUARTERLY,
        BillingFrequency.SEMI_ANNUAL,
        BillingFrequency.ANNUAL,
    ])
        .default(BillingFrequency.MONTHLY),
    billingDayOfMonth: z.number().int().min(1).max(28).default(1),
    startDate: z.coerce.date(),
    endDate: z.coerce.date().optional().nullable(),
    status: z
        .enum([
        RecurringScheduleStatus.ACTIVE,
        RecurringScheduleStatus.PAUSED,
        RecurringScheduleStatus.CANCELLED,
        RecurringScheduleStatus.COMPLETED,
    ])
        .default(RecurringScheduleStatus.ACTIVE),
    paymentTerms: z.string().max(100).default('Net 30'),
    taxRate: z.number().min(0).max(1).default(0),
    gracePeriodDays: z.number().int().min(0).default(15),
    sendInvoiceDaysBefore: z.number().int().min(0).default(0),
    enablePaymentReminders: z.boolean().default(true),
    reminderDaysBeforeDue: z.string().default('[14,7,3,1]'),
    notes: z.string().optional().nullable(),
    items: z.array(recurringScheduleItemSchema).min(1, 'At least one line item is required'),
});
export const updateRecurringScheduleSchema = createRecurringScheduleSchema.partial();
export const listRecurringSchedulesSchema = paginationSchema.merge(searchSchema).extend({
    accountId: z.string().uuid().optional(),
    contractId: z.string().uuid().optional(),
    status: z
        .enum([
        RecurringScheduleStatus.ACTIVE,
        RecurringScheduleStatus.PAUSED,
        RecurringScheduleStatus.CANCELLED,
        RecurringScheduleStatus.COMPLETED,
    ])
        .optional(),
});
// ============================================
// Billing Alert Schemas
// ============================================
export const listAlertsSchema = paginationSchema.extend({
    alertType: z
        .enum([
        AlertType.OVERDUE_INVOICE,
        AlertType.UPCOMING_BILLING,
        AlertType.PAYMENT_REMINDER_SENT,
        AlertType.CONTRACT_EXPIRING,
        AlertType.PAYMENT_RECEIVED,
    ])
        .optional(),
    isRead: z.coerce.boolean().optional(),
    severity: z
        .enum([AlertSeverity.INFO, AlertSeverity.WARNING, AlertSeverity.CRITICAL])
        .optional(),
});
// ============================================
// Expense Schemas
// ============================================
export const createExpenseSchema = z.object({
    categoryId: z.string().uuid('Invalid category'),
    businessProfileId: z.string().uuid().optional().nullable(),
    description: z.string().min(1, 'Description is required').max(500),
    amount: z.number().positive('Amount must be greater than zero'),
    expenseDate: z.coerce.date().optional(),
    vendor: z.string().max(255).optional().nullable(),
    referenceNumber: z.string().max(100).optional().nullable(),
    isRecurring: z.boolean().default(false),
    recurrenceFrequency: z
        .enum([
        ExpenseRecurrence.WEEKLY,
        ExpenseRecurrence.MONTHLY,
        ExpenseRecurrence.QUARTERLY,
        ExpenseRecurrence.ANNUAL,
    ])
        .optional()
        .nullable(),
    isTaxDeductible: z.boolean().default(false),
    taxCategory: z.string().max(50).optional().nullable(),
    paymentDueDay: z.number().int().min(1).max(31).optional().nullable(),
    chargeAccountId: z.string().uuid().optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateExpenseSchema = createExpenseSchema.partial();
export const importExpensesSchema = z.object({
    expenses: z.array(createExpenseSchema).min(1, 'At least one expense is required').max(1000, 'Too many rows — max 1000 per import'),
});
export const listExpensesSchema = paginationSchema.merge(searchSchema).extend({
    categoryId: z.string().uuid().optional(),
    businessProfileId: z.string().uuid().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    isTaxDeductible: z.coerce.boolean().optional(),
});
export const expenseAuditReportSchema = z.object({
    dateFrom: z.string().min(1, 'Start date is required'),
    dateTo: z.string().min(1, 'End date is required'),
    businessProfileId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
});
// ============================================
// Subscription Schemas
// ============================================
export const createSubscriptionSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    provider: z.string().max(255).optional().nullable(),
    description: z.string().max(500).optional().nullable(),
    businessProfileId: z.string().uuid().optional().nullable(),
    cost: z.number().positive('Cost must be greater than zero'),
    billingCycle: z
        .enum([
        SubscriptionBillingCycle.MONTHLY,
        SubscriptionBillingCycle.QUARTERLY,
        SubscriptionBillingCycle.SEMI_ANNUAL,
        SubscriptionBillingCycle.ANNUAL,
    ])
        .default(SubscriptionBillingCycle.MONTHLY),
    renewalDate: z.coerce.date().optional().nullable(),
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional().nullable(),
    status: z
        .enum([
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.CANCELLED,
        SubscriptionStatus.PAUSED,
        SubscriptionStatus.TRIAL,
        SubscriptionStatus.EXPIRING,
    ])
        .default(SubscriptionStatus.ACTIVE),
    category: z.string().max(100).optional().nullable(),
    url: z.string().max(500).optional().nullable(),
    isTaxDeductible: z.boolean().default(false),
    taxCategory: z.string().max(50).optional().nullable(),
    chargeAccountId: z.string().uuid().optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateSubscriptionSchema = createSubscriptionSchema.partial();
export const listSubscriptionsSchema = paginationSchema.merge(searchSchema).extend({
    billingCycle: z
        .enum([
        SubscriptionBillingCycle.MONTHLY,
        SubscriptionBillingCycle.QUARTERLY,
        SubscriptionBillingCycle.SEMI_ANNUAL,
        SubscriptionBillingCycle.ANNUAL,
    ])
        .optional(),
    status: z
        .enum([
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.CANCELLED,
        SubscriptionStatus.PAUSED,
        SubscriptionStatus.TRIAL,
        SubscriptionStatus.EXPIRING,
    ])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
});
// ============================================
// Loan / Mortgage Schemas
// ============================================
export const createLoanSchema = z.object({
    businessProfileId: z.string().uuid().optional().nullable(),
    assetId: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Name is required').max(255),
    lender: z.string().max(255).optional().nullable(),
    description: z.string().max(500).optional().nullable(),
    loanType: z
        .enum([LoanType.MORTGAGE, LoanType.AUTO, LoanType.EQUIPMENT, LoanType.BUSINESS, LoanType.PERSONAL, LoanType.OTHER])
        .default(LoanType.MORTGAGE),
    originalAmount: z.number().positive('Amount must be greater than zero'),
    interestRate: z.number().min(0).max(100),
    termMonths: z.number().int().positive(),
    startDate: z.coerce.date(),
    maturityDate: z.coerce.date().optional().nullable(),
    monthlyPayment: z.number().nonnegative(),
    escrowTax: z.number().nonnegative().default(0),
    escrowInsurance: z.number().nonnegative().default(0),
    extraPrincipal: z.number().nonnegative().default(0),
    currentBalance: z.number().nonnegative().optional().nullable(),
    status: z
        .enum([LoanStatus.ACTIVE, LoanStatus.PAID_OFF, LoanStatus.DEFERRED, LoanStatus.DEFAULT])
        .default(LoanStatus.ACTIVE),
    isInterestDeductible: z.boolean().default(true),
    isTaxEscrowDeductible: z.boolean().default(true),
    isInsuranceDeductible: z.boolean().default(false),
    isInterestOnly: z.boolean().default(false),
    interestOnlyEndDate: z.coerce.date().optional().nullable(),
    taxCategory: z.string().max(50).optional().nullable(),
    paymentDueDay: z.number().int().min(1).max(31).optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateLoanSchema = createLoanSchema.partial();
export const listLoansSchema = paginationSchema.merge(searchSchema).extend({
    loanType: z
        .enum([LoanType.MORTGAGE, LoanType.AUTO, LoanType.EQUIPMENT, LoanType.BUSINESS, LoanType.PERSONAL, LoanType.OTHER])
        .optional(),
    status: z
        .enum([LoanStatus.ACTIVE, LoanStatus.PAID_OFF, LoanStatus.DEFERRED, LoanStatus.DEFAULT])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
});
// ============================================
// Charge Account Schemas
// ============================================
export const createChargeAccountSchema = z.object({
    businessProfileId: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Name is required').max(255),
    issuer: z.string().max(255).optional().nullable(),
    description: z.string().max(500).optional().nullable(),
    accountType: z
        .enum([ChargeAccountType.CREDIT_CARD, ChargeAccountType.STORE_CARD, ChargeAccountType.BUSINESS_LINE])
        .default(ChargeAccountType.CREDIT_CARD),
    creditLimit: z.number().nonnegative().default(0),
    currentBalance: z.number().nonnegative().default(0),
    interestRate: z.number().min(0).max(100).default(0),
    minimumPayment: z.number().nonnegative().default(0),
    desiredPayment: z.number().nonnegative().default(0),
    paymentDueDay: z.number().int().min(1).max(31).default(1),
    statementCloseDay: z.number().int().min(1).max(31).optional().nullable(),
    status: z
        .enum([ChargeAccountStatus.ACTIVE, ChargeAccountStatus.CLOSED, ChargeAccountStatus.FROZEN])
        .default(ChargeAccountStatus.ACTIVE),
    autopayEnabled: z.boolean().default(false),
    isInterestDeductible: z.boolean().default(true),
    taxCategory: z.string().max(50).optional().nullable(),
    last4: z.string().max(4).optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateChargeAccountSchema = createChargeAccountSchema.partial();
export const listChargeAccountsSchema = paginationSchema.merge(searchSchema).extend({
    accountType: z
        .enum([ChargeAccountType.CREDIT_CARD, ChargeAccountType.STORE_CARD, ChargeAccountType.BUSINESS_LINE])
        .optional(),
    status: z
        .enum([ChargeAccountStatus.ACTIVE, ChargeAccountStatus.CLOSED, ChargeAccountStatus.FROZEN])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
});
// ============================================
// Payment Schedule Schemas
// ============================================
export const markPaymentPaidSchema = z.object({
    source: z.enum(['loan', 'charge_account', 'subscription', 'expense']),
    sourceId: z.string().uuid(),
    dueDate: z.coerce.date(),
    paidAmount: z.number().nonnegative().optional().nullable(),
    notes: z.string().max(500).optional().nullable(),
});
export const slidePaymentSchema = z.object({
    source: z.enum(['loan', 'charge_account', 'subscription', 'expense']),
    sourceId: z.string().uuid(),
    dueDate: z.coerce.date(),
    newScheduledDate: z.coerce.date(),
    notes: z.string().max(500).optional().nullable(),
});
export const unmarkPaymentSchema = z.object({
    source: z.enum(['loan', 'charge_account', 'subscription', 'expense']),
    sourceId: z.string().uuid(),
    dueDate: z.coerce.date(),
});
// ============================================
// Budget Schemas
// ============================================
export const createBudgetSchema = z.object({
    categoryId: z.string().uuid('Invalid category'),
    businessProfileId: z.string().uuid().optional().nullable(),
    periodType: z
        .enum([
        BudgetPeriodType.WEEKLY,
        BudgetPeriodType.MONTHLY,
        BudgetPeriodType.QUARTERLY,
        BudgetPeriodType.ANNUAL,
    ])
        .default(BudgetPeriodType.MONTHLY),
    periodStart: z.coerce.date(),
    periodEnd: z.coerce.date(),
    budgetAmount: z.number().positive('Budget amount must be greater than zero'),
    notes: z.string().optional().nullable(),
});
export const updateBudgetSchema = createBudgetSchema.partial();
export const listBudgetsSchema = paginationSchema.extend({
    periodType: z
        .enum([
        BudgetPeriodType.WEEKLY,
        BudgetPeriodType.MONTHLY,
        BudgetPeriodType.QUARTERLY,
        BudgetPeriodType.ANNUAL,
    ])
        .optional(),
    businessProfileId: z.string().uuid().optional(),
});
export const budgetVsActualSchema = z.object({
    periodType: z
        .enum([
        BudgetPeriodType.WEEKLY,
        BudgetPeriodType.MONTHLY,
        BudgetPeriodType.QUARTERLY,
        BudgetPeriodType.ANNUAL,
    ])
        .default(BudgetPeriodType.MONTHLY),
    periodStart: z.string(),
    periodEnd: z.string(),
    businessProfileId: z.string().uuid().optional(),
});
// ============================================
// Lookup Value Schemas
// ============================================
export const createLookupValueSchema = z.object({
    lookupType: z.string().min(1, 'Lookup type is required').max(100),
    code: z.string().min(1, 'Code is required').max(100),
    label: z.string().min(1, 'Label is required').max(255),
    description: z.string().max(500).optional().nullable(),
    parentId: z.string().uuid().optional().nullable(),
    sortOrder: z.number().int().min(0).default(0),
    isSystem: z.boolean().default(false),
    metadata: z.string().optional().nullable(),
});
export const updateLookupValueSchema = z.object({
    label: z.string().min(1).max(255).optional(),
    description: z.string().max(500).optional().nullable(),
    parentId: z.string().uuid().optional().nullable(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    metadata: z.string().optional().nullable(),
});
export const listLookupValuesSchema = paginationSchema.merge(searchSchema).extend({
    lookupType: z.string().max(100).optional(),
    parentId: z.string().uuid().optional(),
    isActive: z.coerce.boolean().optional(),
});
// ============================================
// Document Schemas
// ============================================
export const createDocumentMetadataSchema = z
    .object({
    accountId: z.string().uuid().optional().nullable(),
    contactId: z.string().uuid().optional().nullable(),
    dealId: z.string().uuid().optional().nullable(),
    ticketId: z.string().uuid().optional().nullable(),
    invoiceId: z.string().uuid().optional().nullable(),
    contractId: z.string().uuid().optional().nullable(),
    expenseId: z.string().uuid().optional().nullable(),
    category: z.string().max(100).optional().nullable(),
    tags: z.string().optional().nullable(),
    description: z.string().max(500).optional().nullable(),
})
    .refine((data) => data.accountId || data.contactId || data.dealId || data.ticketId || data.invoiceId || data.contractId || data.expenseId, 'Document must be attached to at least one entity');
export const updateDocumentSchema = z.object({
    category: z.string().max(100).optional().nullable(),
    tags: z.string().optional().nullable(),
    description: z.string().max(500).optional().nullable(),
});
export const listDocumentsSchema = paginationSchema.merge(searchSchema).extend({
    accountId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    ticketId: z.string().uuid().optional(),
    invoiceId: z.string().uuid().optional(),
    contractId: z.string().uuid().optional(),
    expenseId: z.string().uuid().optional(),
    category: z.string().max(100).optional(),
});
// ============================================
// Activity Schemas
// ============================================
export const createActivitySchema = z
    .object({
    accountId: z.string().uuid().optional().nullable(),
    contactId: z.string().uuid().optional().nullable(),
    dealId: z.string().uuid().optional().nullable(),
    ticketId: z.string().uuid().optional().nullable(),
    invoiceId: z.string().uuid().optional().nullable(),
    contractId: z.string().uuid().optional().nullable(),
    activityType: z.enum([
        ActivityType.CALL,
        ActivityType.EMAIL,
        ActivityType.MEETING,
        ActivityType.NOTE,
        ActivityType.FOLLOW_UP,
        ActivityType.DEMO,
        ActivityType.TASK,
        ActivityType.FILING,
        ActivityType.RESEARCH,
        ActivityType.OTHER,
    ]),
    title: z.string().min(1, 'Title is required').max(255),
    description: z.string().optional().nullable(),
    activityDate: z.coerce.date().optional(),
    durationMinutes: z.number().int().min(0).optional().nullable(),
    category: z.string().max(100).optional().nullable(),
    tags: z.string().optional().nullable(),
})
    .refine((data) => data.accountId || data.contactId || data.dealId || data.ticketId || data.invoiceId || data.contractId, 'Activity must be attached to at least one entity');
export const updateActivitySchema = z.object({
    activityType: z
        .enum([
        ActivityType.CALL,
        ActivityType.EMAIL,
        ActivityType.MEETING,
        ActivityType.NOTE,
        ActivityType.FOLLOW_UP,
        ActivityType.DEMO,
        ActivityType.TASK,
        ActivityType.FILING,
        ActivityType.RESEARCH,
        ActivityType.OTHER,
    ])
        .optional(),
    title: z.string().min(1).max(255).optional(),
    description: z.string().optional().nullable(),
    activityDate: z.coerce.date().optional(),
    durationMinutes: z.number().int().min(0).optional().nullable(),
    category: z.string().max(100).optional().nullable(),
    tags: z.string().optional().nullable(),
});
export const listActivitiesSchema = paginationSchema.merge(searchSchema).extend({
    accountId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    dealId: z.string().uuid().optional(),
    ticketId: z.string().uuid().optional(),
    invoiceId: z.string().uuid().optional(),
    contractId: z.string().uuid().optional(),
    activityType: z.string().max(50).optional(),
    category: z.string().max(100).optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    performedBy: z.string().uuid().optional(),
});
// ============================================
// Calendar Schemas
// ============================================
export const listCalendarEventsSchema = z.object({
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    eventType: z.string().max(50).optional(),
});
// ============================================
// Approval Workflow Schemas
// ============================================
export const approvalChainStepSchema = z.object({
    approverId: z.string().uuid(),
    stepOrder: z.number().int().positive(),
    isRequired: z.boolean().default(true),
});
export const createApprovalChainSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
    entityType: z.enum([
        ApprovalEntityType.TICKET,
        ApprovalEntityType.DOCUMENT,
        ApprovalEntityType.INVOICE,
        ApprovalEntityType.CONTRACT,
        ApprovalEntityType.EXPENSE,
    ]),
    steps: z.array(approvalChainStepSchema).min(1, 'At least one step is required'),
});
export const listApprovalChainsSchema = paginationSchema.extend({
    entityType: z.string().max(50).optional(),
});
export const submitApprovalRequestSchema = z.object({
    chainId: z.string().uuid(),
    entityType: z.enum([
        ApprovalEntityType.TICKET,
        ApprovalEntityType.DOCUMENT,
        ApprovalEntityType.INVOICE,
        ApprovalEntityType.CONTRACT,
        ApprovalEntityType.EXPENSE,
    ]),
    entityId: z.string().uuid(),
});
export const approvalActionSchema = z.object({
    action: z.enum([ApprovalAction.APPROVED, ApprovalAction.REJECTED]),
    comment: z.string().max(2000).optional().nullable(),
});
export const listApprovalRequestsSchema = paginationSchema.extend({
    entityType: z.string().max(50).optional(),
    entityId: z.string().uuid().optional(),
    status: z.enum([
        ApprovalStatus.PENDING,
        ApprovalStatus.APPROVED,
        ApprovalStatus.REJECTED,
        ApprovalStatus.CANCELLED,
    ]).optional(),
});
// ============================================
// Report Builder Schemas
// ============================================
export const createReportSchema = z.object({
    id: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Name is required').max(255),
    description: z.string().max(500).optional().nullable(),
    dataSource: z.enum([
        ReportDataSource.INVOICES,
        ReportDataSource.ACCOUNTS,
        ReportDataSource.CONTACTS,
        ReportDataSource.DEALS,
        ReportDataSource.TICKETS,
        ReportDataSource.EXPENSES,
        ReportDataSource.ACTIVITIES,
        ReportDataSource.PAYMENTS,
        ReportDataSource.CONTRACTS,
        ReportDataSource.SUBSCRIPTIONS,
    ]),
    configJson: z.string().min(1, 'Configuration is required'),
    isShared: z.boolean().default(false),
});
export const listReportsSchema = paginationSchema.merge(searchSchema);
export const executeReportSchema = z.object({
    dataSource: z.enum([
        ReportDataSource.INVOICES,
        ReportDataSource.ACCOUNTS,
        ReportDataSource.CONTACTS,
        ReportDataSource.DEALS,
        ReportDataSource.TICKETS,
        ReportDataSource.EXPENSES,
        ReportDataSource.ACTIVITIES,
        ReportDataSource.PAYMENTS,
        ReportDataSource.CONTRACTS,
        ReportDataSource.SUBSCRIPTIONS,
    ]),
    filters: z.record(z.unknown()).default({}),
});
// ============================================
// Document Search Schema
// ============================================
export const searchDocumentsSchema = paginationSchema.extend({
    q: z.string().min(2, 'Search term must be at least 2 characters'),
    accountId: z.string().uuid().optional(),
    category: z.string().max(100).optional(),
});
// ============================================
// Scheduled Report Schemas
// ============================================
export const createScheduledReportSchema = z.object({
    reportId: z.string().uuid(),
    name: z.string().min(1, 'Name is required').max(255),
    scheduleTime: z.string().regex(/^\d{2}:\d{2}$/, 'Time must be in HH:MM format').default('08:00'),
    recipientEmails: z.string().min(1, 'At least one recipient is required'),
});
export const updateScheduledReportSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    scheduleTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    recipientEmails: z.string().optional(),
    isEnabled: z.boolean().optional(),
});
export const listScheduledReportsSchema = paginationSchema;
// ============================================
// Outlook Sync Schemas
// ============================================
export const syncEventSchema = z.object({
    entityType: z.string().min(1).max(50),
    entityId: z.string().uuid(),
});
// ============================================
// Project Management Schemas
// ============================================
// Project
export const createProjectSchema = z.object({
    accountId: z.string().uuid().optional().nullable(),
    contactId: z.string().uuid().optional().nullable(),
    contractId: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Project name is required').max(255),
    description: z.string().optional().nullable(),
    projectType: z
        .enum([
        ProjectType.CONSULTING,
        ProjectType.SOFTWARE_DESIGN,
        ProjectType.SOFTWARE_DEV,
        ProjectType.STAFF_AUGMENTATION,
        ProjectType.FIXED_PRICE,
        ProjectType.RETAINER,
        ProjectType.INTERNAL,
    ])
        .default(ProjectType.CONSULTING),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).default(Priority.MEDIUM),
    billingType: z
        .enum([
        ProjectBillingType.TIME_AND_MATERIALS,
        ProjectBillingType.FIXED_PRICE,
        ProjectBillingType.RETAINER,
        ProjectBillingType.MILESTONE,
        ProjectBillingType.NOT_BILLABLE,
    ])
        .default(ProjectBillingType.TIME_AND_MATERIALS),
    budgetAmount: z.number().nonnegative().optional().nullable(),
    budgetHours: z.number().nonnegative().optional().nullable(),
    hourlyRate: z.number().nonnegative().optional().nullable(),
    startDate: z.coerce.date().optional().nullable(),
    targetEndDate: z.coerce.date().optional().nullable(),
    projectManagerId: z.string().uuid().optional().nullable(),
    tags: z.string().optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateProjectSchema = createProjectSchema.partial().extend({
    status: z
        .enum([
        ProjectStatus.PLANNING,
        ProjectStatus.ACTIVE,
        ProjectStatus.ON_HOLD,
        ProjectStatus.COMPLETED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ARCHIVED,
    ])
        .optional(),
    completionPct: z.number().min(0).max(100).optional(),
    actualEndDate: z.coerce.date().optional().nullable(),
});
export const listProjectsSchema = paginationSchema.merge(searchSchema).extend({
    status: z
        .enum([
        ProjectStatus.PLANNING,
        ProjectStatus.ACTIVE,
        ProjectStatus.ON_HOLD,
        ProjectStatus.COMPLETED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ARCHIVED,
    ])
        .optional(),
    projectType: z.string().max(50).optional(),
    accountId: z.string().uuid().optional(),
    projectManagerId: z.string().uuid().optional(),
});
// Phase
export const createPhaseSchema = z.object({
    projectId: z.string().uuid(),
    name: z.string().min(1, 'Phase name is required').max(255),
    description: z.string().optional().nullable(),
    sortOrder: z.number().int().min(0).default(0),
    startDate: z.coerce.date().optional().nullable(),
    targetEndDate: z.coerce.date().optional().nullable(),
    isMilestone: z.boolean().default(false),
    milestoneDate: z.coerce.date().optional().nullable(),
    deliverableDesc: z.string().optional().nullable(),
});
export const updatePhaseSchema = createPhaseSchema.partial().omit({ projectId: true }).extend({
    status: z
        .enum([PhaseStatus.NOT_STARTED, PhaseStatus.IN_PROGRESS, PhaseStatus.COMPLETED, PhaseStatus.SKIPPED])
        .optional(),
    completionPct: z.number().min(0).max(100).optional(),
    actualEndDate: z.coerce.date().optional().nullable(),
});
// Project Task
export const createProjectTaskSchema = z.object({
    projectId: z.string().uuid(),
    phaseId: z.string().uuid().optional().nullable(),
    parentTaskId: z.string().uuid().optional().nullable(),
    title: z.string().min(1, 'Task title is required').max(255),
    description: z.string().optional().nullable(),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).default(Priority.MEDIUM),
    assigneeId: z.string().uuid().optional().nullable(),
    sortOrder: z.number().int().min(0).default(0),
    estimatedHours: z.number().nonnegative().optional().nullable(),
    startDate: z.coerce.date().optional().nullable(),
    dueDate: z.coerce.date().optional().nullable(),
    dependsOnTaskId: z.string().uuid().optional().nullable(),
    tags: z.string().optional().nullable(),
});
export const updateProjectTaskSchema = createProjectTaskSchema.partial().omit({ projectId: true }).extend({
    status: z
        .enum([
        ProjectTaskStatus.TODO,
        ProjectTaskStatus.IN_PROGRESS,
        ProjectTaskStatus.IN_REVIEW,
        ProjectTaskStatus.BLOCKED,
        ProjectTaskStatus.DONE,
        ProjectTaskStatus.CANCELLED,
    ])
        .optional(),
});
export const listProjectTasksSchema = paginationSchema.extend({
    projectId: z.string().uuid().optional(),
    phaseId: z.string().uuid().optional(),
    status: z
        .enum([
        ProjectTaskStatus.TODO,
        ProjectTaskStatus.IN_PROGRESS,
        ProjectTaskStatus.IN_REVIEW,
        ProjectTaskStatus.BLOCKED,
        ProjectTaskStatus.DONE,
        ProjectTaskStatus.CANCELLED,
    ])
        .optional(),
    assigneeId: z.string().uuid().optional(),
    search: z.string().optional(),
});
// Time Entry
export const createTimeEntrySchema = z.object({
    projectId: z.string().uuid(),
    taskId: z.string().uuid().optional().nullable(),
    entryDate: z.coerce.date().optional(),
    hours: z.number().positive('Hours must be greater than zero').max(24),
    description: z.string().max(500).optional().nullable(),
    isBillable: z.boolean().default(true),
    billingRate: z.number().nonnegative().optional().nullable(),
});
export const updateTimeEntrySchema = createTimeEntrySchema.partial().omit({ projectId: true });
export const listTimeEntriesSchema = paginationSchema.extend({
    projectId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
    userId: z.string().uuid().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    isBillable: z.coerce.boolean().optional(),
    isInvoiced: z.coerce.boolean().optional(),
});
// Requirement
export const createRequirementSchema = z.object({
    projectId: z.string().uuid(),
    requirementNumber: z.string().min(1, 'Requirement number is required').max(50),
    title: z.string().min(1, 'Title is required').max(255),
    description: z.string().optional().nullable(),
    category: z
        .enum([
        RequirementCategory.FUNCTIONAL,
        RequirementCategory.NON_FUNCTIONAL,
        RequirementCategory.BUSINESS,
        RequirementCategory.TECHNICAL,
        RequirementCategory.COMPLIANCE,
        RequirementCategory.SECURITY,
    ])
        .default(RequirementCategory.FUNCTIONAL),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).default(Priority.MEDIUM),
    source: z.string().max(255).optional().nullable(),
    acceptanceCriteria: z.string().optional().nullable(),
    phaseId: z.string().uuid().optional().nullable(),
});
export const updateRequirementSchema = createRequirementSchema.partial().omit({ projectId: true }).extend({
    status: z
        .enum([
        RequirementStatus.DRAFT,
        RequirementStatus.APPROVED,
        RequirementStatus.IN_PROGRESS,
        RequirementStatus.IMPLEMENTED,
        RequirementStatus.VERIFIED,
        RequirementStatus.DEFERRED,
        RequirementStatus.REJECTED,
    ])
        .optional(),
});
export const listRequirementsSchema = paginationSchema.extend({
    projectId: z.string().uuid().optional(),
    category: z.string().max(50).optional(),
    status: z.string().max(50).optional(),
    search: z.string().optional(),
});
// Requirement Link
export const createRequirementLinkSchema = z.object({
    requirementId: z.string().uuid(),
    taskId: z.string().uuid().optional().nullable(),
    riskId: z.string().uuid().optional().nullable(),
    deliverableId: z.string().uuid().optional().nullable(),
    linkType: z
        .enum([
        RequirementLinkType.IMPLEMENTS,
        RequirementLinkType.TESTS,
        RequirementLinkType.MITIGATES,
        RequirementLinkType.VALIDATES,
        RequirementLinkType.DEPENDS_ON,
    ])
        .default(RequirementLinkType.IMPLEMENTS),
    notes: z.string().max(500).optional().nullable(),
});
// Risk
export const createRiskSchema = z.object({
    projectId: z.string().uuid(),
    riskNumber: z.string().min(1, 'Risk number is required').max(50),
    title: z.string().min(1, 'Title is required').max(255),
    description: z.string().optional().nullable(),
    category: z
        .enum([
        RiskCategory.TECHNICAL,
        RiskCategory.SCHEDULE,
        RiskCategory.BUDGET,
        RiskCategory.RESOURCE,
        RiskCategory.SCOPE,
        RiskCategory.COMPLIANCE,
        RiskCategory.EXTERNAL,
    ])
        .default(RiskCategory.TECHNICAL),
    likelihood: z
        .enum([RiskLikelihood.VERY_LOW, RiskLikelihood.LOW, RiskLikelihood.MEDIUM, RiskLikelihood.HIGH, RiskLikelihood.VERY_HIGH])
        .default(RiskLikelihood.MEDIUM),
    impact: z
        .enum([RiskLikelihood.VERY_LOW, RiskLikelihood.LOW, RiskLikelihood.MEDIUM, RiskLikelihood.HIGH, RiskLikelihood.VERY_HIGH])
        .default(RiskLikelihood.MEDIUM),
    mitigationPlan: z.string().optional().nullable(),
    contingencyPlan: z.string().optional().nullable(),
    riskOwnerId: z.string().uuid().optional().nullable(),
    targetResolution: z.coerce.date().optional().nullable(),
});
export const updateRiskSchema = createRiskSchema.partial().omit({ projectId: true }).extend({
    status: z
        .enum([
        RiskStatus.IDENTIFIED,
        RiskStatus.ANALYZING,
        RiskStatus.MITIGATING,
        RiskStatus.RESOLVED,
        RiskStatus.ACCEPTED,
        RiskStatus.CLOSED,
    ])
        .optional(),
    resolvedDate: z.coerce.date().optional().nullable(),
});
export const listRisksSchema = paginationSchema.extend({
    projectId: z.string().uuid().optional(),
    status: z.string().max(50).optional(),
    category: z.string().max(50).optional(),
    search: z.string().optional(),
});
// Deliverable
export const createDeliverableSchema = z.object({
    projectId: z.string().uuid(),
    phaseId: z.string().uuid().optional().nullable(),
    name: z.string().min(1, 'Name is required').max(255),
    description: z.string().optional().nullable(),
    dueDate: z.coerce.date().optional().nullable(),
    documentId: z.string().uuid().optional().nullable(),
    notes: z.string().optional().nullable(),
});
export const updateDeliverableSchema = createDeliverableSchema.partial().omit({ projectId: true }).extend({
    status: z
        .enum([
        DeliverableStatus.PENDING,
        DeliverableStatus.IN_PROGRESS,
        DeliverableStatus.SUBMITTED,
        DeliverableStatus.APPROVED,
        DeliverableStatus.REJECTED,
        DeliverableStatus.REVISION,
    ])
        .optional(),
    submittedDate: z.coerce.date().optional().nullable(),
});
export const listDeliverablesSchema = paginationSchema.extend({
    projectId: z.string().uuid().optional(),
    phaseId: z.string().uuid().optional(),
    status: z.string().max(50).optional(),
});
// Resource Assignment
export const createResourceAssignmentSchema = z.object({
    projectId: z.string().uuid(),
    userId: z.string().uuid(),
    roleName: z.string().min(1, 'Role is required').max(100),
    allocationPct: z.number().min(0).max(100).default(100),
    billingRate: z.number().nonnegative().optional().nullable(),
    startDate: z.coerce.date().optional().nullable(),
    endDate: z.coerce.date().optional().nullable(),
    notes: z.string().max(500).optional().nullable(),
});
export const updateResourceAssignmentSchema = createResourceAssignmentSchema.partial().omit({ projectId: true, userId: true }).extend({
    isActive: z.boolean().optional(),
});
// ============================================
// Helpdesk Schemas
// ============================================
// SLA Policy Schemas
export const createSlaPolicySchema = z.object({
    name: z.string().min(1).max(100),
    applicationId: z.string().uuid().nullable().optional(),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]),
    responseTimeMinutes: z.number().int().positive(),
    resolutionTimeMinutes: z.number().int().positive(),
    businessHoursOnly: z.boolean().optional().default(false),
});
export const updateSlaPolicySchema = createSlaPolicySchema.partial().extend({
    isActive: z.boolean().optional(),
});
export const listSlaPoliciesSchema = paginationSchema.extend({
    applicationId: z.string().uuid().optional(),
    isActive: z.coerce.boolean().optional(),
});
// Escalation Rule Schemas
export const createEscalationRuleSchema = z.object({
    slaPolicyId: z.string().uuid(),
    escalationType: z.enum([EscalationType.RESPONSE_APPROACHING, EscalationType.RESPONSE_BREACH, EscalationType.RESOLUTION_APPROACHING, EscalationType.RESOLUTION_BREACH]),
    thresholdPct: z.number().int().min(1).max(100),
    notifyUserId: z.string().uuid().nullable().optional(),
    notifyRole: z.string().max(20).nullable().optional(),
    action: z.enum([EscalationAction.NOTIFY, EscalationAction.REASSIGN, EscalationAction.ESCALATE_PRIORITY]).optional().default('notify'),
    sortOrder: z.number().int().optional().default(0),
});
// Notification Preferences Schema
export const updateNotificationPrefsSchema = z.object({
    emailNewTicket: z.boolean().optional(),
    emailTicketAssigned: z.boolean().optional(),
    emailTicketReply: z.boolean().optional(),
    emailTicketResolved: z.boolean().optional(),
    emailSlaWarning: z.boolean().optional(),
    emailSlaBreach: z.boolean().optional(),
    smsNewTicket: z.boolean().optional(),
    smsSlaBreach: z.boolean().optional(),
    smsTicketAssigned: z.boolean().optional(),
    digestFrequency: z.enum([DigestFrequency.REALTIME, DigestFrequency.HOURLY, DigestFrequency.DAILY]).optional(),
    quietHoursStart: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(),
    quietHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(),
});
// Email Template Schema
export const upsertEmailTemplateSchema = z.object({
    name: z.string().min(1).max(100),
    subjectTemplate: z.string().min(1).max(500),
    bodyTemplate: z.string().min(1),
    isActive: z.boolean().optional().default(true),
});
// Canned Response Schemas
export const createCannedResponseSchema = z.object({
    title: z.string().min(1).max(200),
    content: z.string().min(1),
    category: z.string().max(100).nullable().optional(),
    shortcut: z.string().max(50).nullable().optional(),
    isShared: z.boolean().optional().default(true),
});
export const updateCannedResponseSchema = createCannedResponseSchema.partial();
export const listCannedResponsesSchema = paginationSchema.extend({
    search: z.string().optional(),
    category: z.string().optional(),
});
// Knowledge Base Schemas
export const createKbCategorySchema = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/),
    description: z.string().max(500).nullable().optional(),
    sortOrder: z.number().int().optional().default(0),
    parentId: z.string().uuid().nullable().optional(),
});
export const createKbArticleSchema = z.object({
    title: z.string().min(1).max(255),
    slug: z.string().min(1).max(255).regex(/^[a-z0-9-]+$/),
    content: z.string().min(1),
    categoryId: z.string().uuid().nullable().optional(),
    applicationId: z.string().uuid().nullable().optional(),
    status: z.enum([KbArticleStatus.DRAFT, KbArticleStatus.PUBLISHED, KbArticleStatus.ARCHIVED]).optional().default('draft'),
    isPublic: z.boolean().optional().default(false),
    tags: z.string().nullable().optional(),
});
export const updateKbArticleSchema = createKbArticleSchema.partial();
export const listKbArticlesSchema = paginationSchema.extend({
    search: z.string().optional(),
    categoryId: z.string().uuid().optional(),
    applicationId: z.string().uuid().optional(),
    status: z.enum([KbArticleStatus.DRAFT, KbArticleStatus.PUBLISHED, KbArticleStatus.ARCHIVED]).optional(),
    isPublic: z.coerce.boolean().optional(),
});
// Survey Schemas
export const submitSurveySchema = z.object({
    rating: z.number().int().min(1).max(5),
    comment: z.string().max(2000).nullable().optional(),
});
// Ticket Link Schemas
export const createTicketLinkSchema = z.object({
    targetTicketId: z.string().uuid(),
    linkType: z.enum([TicketLinkType.RELATED, TicketLinkType.DUPLICATE, TicketLinkType.BLOCKS, TicketLinkType.BLOCKED_BY, TicketLinkType.PARENT, TicketLinkType.CHILD]),
});
export const mergeTicketsSchema = z.object({
    targetTicketId: z.string().uuid(),
});
// Ticket Watcher Schema
export const addWatcherSchema = z.object({
    userId: z.string().uuid(),
});
// App Status Schemas
export const updateAppStatusSchema = z.object({
    status: z.enum([AppOperationalStatus.OPERATIONAL, AppOperationalStatus.DEGRADED, AppOperationalStatus.PARTIAL_OUTAGE, AppOperationalStatus.MAJOR_OUTAGE, AppOperationalStatus.MAINTENANCE]),
    message: z.string().max(500).nullable().optional(),
});
// Incident Schemas
export const createIncidentSchema = z.object({
    applicationId: z.string().uuid(),
    title: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    severity: z.enum([IncidentSeverity.MINOR, IncidentSeverity.MAJOR, IncidentSeverity.CRITICAL]).optional().default('minor'),
    status: z.enum([IncidentStatus.INVESTIGATING, IncidentStatus.IDENTIFIED, IncidentStatus.MONITORING, IncidentStatus.RESOLVED]).optional().default('investigating'),
});
export const updateIncidentSchema = createIncidentSchema.partial().extend({
    resolvedAt: z.string().datetime().nullable().optional(),
});
// Automation Rule Schemas
export const automationConditionSchema = z.object({
    field: z.string().min(1),
    operator: z.enum([AutomationOperator.EQUALS, AutomationOperator.NOT_EQUALS, AutomationOperator.CONTAINS, AutomationOperator.NOT_CONTAINS, AutomationOperator.IN, AutomationOperator.NOT_IN, AutomationOperator.GREATER_THAN, AutomationOperator.LESS_THAN]),
    value: z.union([z.string(), z.array(z.string())]),
});
export const automationActionSchema = z.object({
    type: z.enum([AutomationActionType.SET_PRIORITY, AutomationActionType.SET_STATUS, AutomationActionType.ASSIGN_TO, AutomationActionType.ADD_TAG, AutomationActionType.SEND_NOTIFICATION, AutomationActionType.ADD_INTERNAL_NOTE, AutomationActionType.SET_CATEGORY]),
    value: z.string(),
});
export const createAutomationRuleSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(500).nullable().optional(),
    triggerEvent: z.enum([AutomationTrigger.TICKET_CREATED, AutomationTrigger.TICKET_UPDATED, AutomationTrigger.TICKET_REPLIED, AutomationTrigger.SLA_APPROACHING, AutomationTrigger.TICKET_IDLE, AutomationTrigger.TICKET_RESOLVED]),
    conditions: z.array(automationConditionSchema).min(0),
    actions: z.array(automationActionSchema).min(1),
    isActive: z.boolean().optional().default(true),
    executionOrder: z.number().int().optional().default(0),
});
export const updateAutomationRuleSchema = createAutomationRuleSchema.partial();
// Maintenance Window Schemas
export const createMaintenanceWindowSchema = z.object({
    applicationId: z.string().uuid(),
    title: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    scheduledStart: z.string().datetime(),
    scheduledEnd: z.string().datetime(),
    notifyBeforeMinutes: z.number().int().min(0).optional().default(1440),
});
export const updateMaintenanceWindowSchema = createMaintenanceWindowSchema.partial().extend({
    status: z.enum([MaintenanceStatus.SCHEDULED, MaintenanceStatus.IN_PROGRESS, MaintenanceStatus.COMPLETED, MaintenanceStatus.CANCELLED]).optional(),
    actualStart: z.string().datetime().nullable().optional(),
    actualEnd: z.string().datetime().nullable().optional(),
});
export const listMaintenanceWindowsSchema = paginationSchema.extend({
    applicationId: z.string().uuid().optional(),
    status: z.enum([MaintenanceStatus.SCHEDULED, MaintenanceStatus.IN_PROGRESS, MaintenanceStatus.COMPLETED, MaintenanceStatus.CANCELLED]).optional(),
});
// Client Portal Schemas
export const clientPortalSubmitTicketSchema = z.object({
    applicationId: z.string().uuid().optional(),
    subject: z.string().min(1).max(255),
    description: z.string().min(1),
    priority: z.enum([Priority.LOW, Priority.MEDIUM, Priority.HIGH, Priority.URGENT]).optional().default('medium'),
    category: z.string().max(100).nullable().optional(),
});
export const clientPortalReplySchema = z.object({
    content: z.string().min(1),
});
// Helpdesk Report Schemas
export const helpdeskReportSchema = paginationSchema.extend({
    period: z.enum(['7d', '30d', '90d', '365d', 'all']).optional().default('30d'),
    applicationId: z.string().uuid().optional(),
    assigneeId: z.string().uuid().optional(),
});
// ============================================
// Tax Scenario Schemas
// ============================================
const filingStatusEnum = z.enum(['single', 'married_filing_jointly', 'married_filing_separately', 'head_of_household']);
const entityTypeEnum = z.enum(['sole_proprietorship', 'single_member_llc', 'partnership', 's_corporation', 'c_corporation']);
export const createTaxScenarioSchema = z.object({
    businessProfileId: z.string().uuid().optional().nullable(),
    name: z.string().min(1).max(200).default('Untitled Scenario'),
    description: z.string().max(500).optional().nullable(),
    taxYear: z.coerce.number().int().min(2000).max(2100),
    baseRevenue: z.number().nonnegative().default(0),
    baseDeductions: z.number().nonnegative().default(0),
    filingStatus: filingStatusEnum.default('single'),
    entityType: entityTypeEnum.default('sole_proprietorship'),
    state: z.string().max(2).optional().nullable(),
    adjustments: z.array(z.object({
        id: z.string(),
        type: z.enum(['capital_purchase', 'expense_add', 'expense_remove', 'revenue_change']),
        name: z.string(),
        amount: z.number(),
        deductibleAmount: z.number(),
        depreciationMethod: z.string().optional(),
        category: z.string().optional(),
    })).optional().nullable(),
    results: z.record(z.unknown()).optional().nullable(),
    isBaseline: z.boolean().default(false),
});
export const updateTaxScenarioSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(500).optional().nullable(),
    baseRevenue: z.number().nonnegative().optional(),
    baseDeductions: z.number().nonnegative().optional(),
    filingStatus: filingStatusEnum.optional(),
    entityType: entityTypeEnum.optional(),
    state: z.string().max(2).optional().nullable(),
    adjustments: z.array(z.object({
        id: z.string(),
        type: z.enum(['capital_purchase', 'expense_add', 'expense_remove', 'revenue_change']),
        name: z.string(),
        amount: z.number(),
        deductibleAmount: z.number(),
        depreciationMethod: z.string().optional(),
        category: z.string().optional(),
    })).optional(),
    results: z.record(z.unknown()).optional(),
});
// ============================================
// Forecast Schemas
// ============================================
export const createForecastSchema = z.object({
    businessProfileId: z.string().uuid().optional().nullable(),
    name: z.string().min(1).max(200).default('Untitled Forecast'),
    description: z.string().max(500).optional().nullable(),
    baseYear: z.coerce.number().int().min(2000).max(2100),
    projectionYears: z.coerce.number().int().min(1).max(10).default(4),
    config: z.object({
        revenueGrowthRates: z.array(z.number()),
        expenseGrowthRates: z.array(z.number()),
        capitalInvestments: z.array(z.number().nonnegative()),
        capitalDeductionPct: z.array(z.number().min(0).max(100)),
        additionalDeductions: z.array(z.number().nonnegative()),
        filingStatus: filingStatusEnum,
        entityType: entityTypeEnum,
        state: z.string().max(2),
        revenueTarget: z.number().nonnegative().nullable(),
        notes: z.string(),
    }).optional().nullable(),
    results: z.array(z.record(z.unknown())).optional().nullable(),
});
export const updateForecastSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(500).optional().nullable(),
    projectionYears: z.coerce.number().int().min(1).max(10).optional(),
    config: z.object({
        revenueGrowthRates: z.array(z.number()),
        expenseGrowthRates: z.array(z.number()),
        capitalInvestments: z.array(z.number().nonnegative()),
        capitalDeductionPct: z.array(z.number().min(0).max(100)),
        additionalDeductions: z.array(z.number().nonnegative()),
        filingStatus: filingStatusEnum,
        entityType: entityTypeEnum,
        state: z.string().max(2),
        revenueTarget: z.number().nonnegative().nullable(),
        notes: z.string(),
    }).optional(),
    results: z.array(z.record(z.unknown())).optional(),
});
//# sourceMappingURL=index.js.map